import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IsMobile } from '../../../../../../utility/BrowserCheck';
import { withFeature } from '../../LaunchDarkly';
import useInitialiseEmbed from '../helpers/useInitialiseEmbed';
import useUnityContext from '../helpers/useUnityContext';
import { StyledMessage } from '../Unity.styled';
import UnityWebPlayer from './UnityWebPlayer';
import { useDetectVisibility } from '@core/components/Unity/helpers/useDetectVisibility';
import { useEmbedMessages } from '@core/components/Unity/helpers/useEmbedMessages';

export const SimulationEditorEmbed = () => {
  const { search } = useLocation();
  const history = useHistory();

  const [visible] = useDetectVisibility();
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const context = useUnityContext();

  const [Hash, debug] = useMemo(() => {
    const params = new URLSearchParams(search);

    return [params.get('Hash'), Boolean(params.get('debug')) || false];
  }, [search]);

  const { simulationData } = useInitialiseEmbed(Hash);

  const [send, postEvent] = useEmbedMessages({
    context,
    Hash,
    visible,
    debug,
  });

  useEffect(() => {
    send('PlayerVisible', visible);

    postEvent('onPlayerVisible')(visible);
  }, [postEvent, send, visible]);

  useEffect(() => {
    // need to debounce this call, as it seems to be sensitive to multiple calls
    const setEditorDebounce = _.debounce((mode) => {
      send('SetEditorMode', mode);
    }, 50);

    /**
     * @param {MessageEvent} event
     */
    const handleEvents = (event) => {
      const { Hash: EventHash, event: eventName, data } = event.data;
      if (EventHash !== Hash) {
        return;
      }
      switch (eventName) {
        // case 'updateSimulation':
        //   return send('UpdateSimData', data);
        case 'createVideo':
          return send('AddVideo', data);
        case 'deleteVideo':
          return send('RemoveVideo', String(data));
        case 'updateVideo':
          return send('UpdateVideo', data);
        case 'createEvent':
          return send('CreateEvent', data);
        case 'deleteEvent':
          return send('DeleteEvent', String(data));
        case 'updateEvent':
          return send('UpdateEvent', data);
        case 'createAnswer':
          return send('CreateAnswer', data);
        case 'deleteAnswer':
          return send('DeleteAnswer', data);
        case 'updateAnswer':
          return send('UpdateAnswer', data);
        case 'play':
          return send('PlayVideo');
        case 'pause':
          return send('PauseVideo');
        case 'setVideo':
          const params = new URLSearchParams(window.location.search);
          const videoId = String(data);
          params.set('videoID', videoId);
          history.replace({ search: `?${params.toString()}` });
          return send('ChangeVideo', data);
        case 'selectInteraction':
          return send('SelectInteraction', data != null ? String(data) : '');
        case 'seek':
          // timestamp in ms
          return send('SeekVideo', Math.round(Number(data)));
        case 'setEditorMode':
          return setEditorDebounce(data);
        case 'updateOffset':
          return send('UpdateOffset', String(data));
        case 'setShowGuidelines':
          return send('TogglePlacementMarkers', data);
        case 'setFocus':
          return send('SetFocused', data);
        case 'resetOffset':
          return send('ResetCameraOrientation');
        default:
        // nothing
      }
    };
    window.addEventListener('message', handleEvents);

    return () => {
      window.removeEventListener('message', handleEvents);
    };
  }, [Hash, history, send]);

  useEffect(() => {
    if (!context) {
      return;
    }

    const handleInteractable = postEvent('onInteractable');
    context.addEventListener('SetModeButtonInteractable', handleInteractable);

    const handleChangeVideo = postEvent('onChangeVideo');
    context.addEventListener('ChangeToNewContent', handleChangeVideo);

    /**
     * @param {any} data
     */
    const handlePlayerLoaded = postEvent('onPlayerLoaded');
    context.addEventListener('OnPlayerLoaded', handlePlayerLoaded);

    const handleInteractionSelected = postEvent('onInteractionSelected');
    context.addEventListener('OnEventSelected', handleInteractionSelected);

    const handleInteractionUpdated = postEvent('onInteractionUpdated');
    context.addEventListener('OnInteractionAdjusted', handleInteractionUpdated);

    const handleContextMenu = postEvent('onContextMenu');
    context.addEventListener('OnContextMenu', handleContextMenu);

    const handleCurrentTime = postEvent('onCurrentTime');
    context.addEventListener('CurrentVideoTime', handleCurrentTime);

    const handleDefaultCoordinates = postEvent('onDefaultCoordinates');
    context.addEventListener('OnCameraFocusChanged', handleDefaultCoordinates);

    const handlePauseVideo = postEvent('onPauseVideo');
    context.addEventListener('VideoPause', handlePauseVideo);

    return () => {
      context.removeEventListener(
        'SetModeButtonInteractable',
        handleInteractable
      );
      context.removeEventListener('ChangeToNewContent', handleChangeVideo);
      context.removeEventListener('OnPlayerLoaded', handlePlayerLoaded);
      context.removeEventListener('OnEventSelected', handleInteractionSelected);
      context.removeEventListener(
        'OnInteractionAdjusted',
        handleInteractionUpdated
      );
      context.removeEventListener('OnContextMenu', handleContextMenu);
      context.removeEventListener('CurrentVideoTime', handleCurrentTime);
      context.removeEventListener(
        'OnCameraFocusChanged',
        handleDefaultCoordinates
      );
      context.removeEventListener('VideoPause', handlePauseVideo);
    };
  }, [context, postEvent]);

  if (!simulationData) {
    return null;
  }

  if (IsMobile()) {
    return (
      <StyledMessage>
        Unfortunately, web preview is currently only available in latest version
        of Google Chrome, Mozilla Firefox, and Microsoft Edge on a computer.
      </StyledMessage>
    );
  }

  return (
    <UnityWebPlayer
      preview
      editor
      simulationData={simulationData}
      context={context}
    />
  );
};

// needs feature HOC to not load this component until features are available
export default withFeature(['unity-build-path'])(SimulationEditorEmbed);
